/* Tour mode — lightweight product walk-through (iter 60).
 *
 * Custom-built (no library) so we stay in the brand's design system and
 * keep the dependency tree small. The overlay paints a dark backdrop
 * with a "cut-out" around the highlighted element + a tooltip card
 * positioned next to it.
 *
 * Flow:
 *   - `TourProvider` mounts once at the App root.
 *   - Reads `?tour=<id>` from the URL on mount and on every location
 *     change. When matched, loads the step definitions and kicks off.
 *   - A step is either:
 *       * "centered" — modal-style, no target element (welcome / end)
 *       * "spotlight" — points at `target` (a data-testid selector),
 *         scrolls it into view, positions tooltip near it.
 *   - Cross-route: if `step.route` differs from the current path, we
 *     navigate and then wait for `target` to render before painting.
 *   - User controls: Next / Prev / Skip / close. ESC also skips.
 *
 * The tour definitions live in `tours.js` so the overlay component
 * stays generic and can be reused for the patient tour later.
 */
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { TOURS } from "./tours";
import api from "@/lib/api";
import { Sparkle, ArrowRight, ArrowLeft, X, CheckCircle } from "@phosphor-icons/react";

const TourCtx = React.createContext(null);

export const useTour = () => useContext(TourCtx);


export function TourProvider({ children }) {
    const [tourId, setTourId] = useState(null);
    const [stepIdx, setStepIdx] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    /* Boot the tour from `?tour=<id>` whenever the URL has one. We strip
       the param immediately so it doesn't replay on Back navigation. */
    useEffect(() => {
        const fromUrl = searchParams.get("tour");
        if (fromUrl && TOURS[fromUrl] && tourId !== fromUrl) {
            setTourId(fromUrl);
            setStepIdx(0);
            const next = new URLSearchParams(searchParams);
            next.delete("tour");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, tourId, setSearchParams]);

    const start = useCallback((id) => {
        if (!TOURS[id]) return;
        setTourId(id);
        setStepIdx(0);
    }, []);

    const stop = useCallback(() => {
        setTourId(null);
        setStepIdx(0);
    }, []);

    /* iter98.90 — fired when a tour reaches its last step. POSTs to
       /doctor/tour-progress so the Tour Library can render a ✓ ribbon
       and bump the "X of N completed" counter. Best-effort: failures
       are swallowed so a brief network blip never throws the doctor
       out of the welcome flow. The patient-side tour also no-ops here
       (the endpoint will simply 401 for the patient role — caught by
       the catch handler). */
    const markComplete = useCallback(async (id) => {
        if (!id) return;
        try {
            await api.post("/doctor/tour-progress", { tour_id: id });
        } catch (_) {
            /* graceful degradation — the doctor still saw the tour */
        }
    }, []);

    const steps = useMemo(() => (tourId ? TOURS[tourId].steps : []), [tourId]);
    const active = !!tourId && stepIdx >= 0 && stepIdx < steps.length;
    const step = active ? steps[stepIdx] : null;

    /* Auto-navigate if the step demands a different route. We push the
       navigation eagerly so the page is mounted by the time we go to
       paint the spotlight. */
    useEffect(() => {
        if (!step?.route) return;
        if (location.pathname !== step.route) {
            navigate(step.route, { replace: false });
        }
    }, [step, location.pathname, navigate]);

    const next = useCallback(() => {
        if (!active) return;
        if (stepIdx + 1 >= steps.length) {
            // Reaching past the last step counts as completion.
            markComplete(tourId);
            stop();
        } else {
            setStepIdx((i) => i + 1);
        }
    }, [active, stepIdx, steps.length, stop, markComplete, tourId]);

    const prev = useCallback(() => {
        setStepIdx((i) => Math.max(0, i - 1));
    }, []);

    /* ESC closes — a hard-stop matches user expectations from any modal */
    useEffect(() => {
        if (!active) return;
        const onKey = (e) => {
            if (e.key === "Escape") stop();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active, stop, next, prev]);

    const value = useMemo(() => ({
        active, tourId, stepIdx, step, steps, start, stop, next, prev,
        totalSteps: steps.length,
    }), [active, tourId, stepIdx, step, steps, start, stop, next, prev]);

    return (
        <TourCtx.Provider value={value}>
            {children}
            {active && <TourOverlay />}
        </TourCtx.Provider>
    );
}


function TourOverlay() {
    const { step, stepIdx, totalSteps, next, prev, stop } = useTour();
    const [rect, setRect] = useState(null); // bounding rect of the target element
    const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0, placement: "bottom" });
    const tooltipRef = useRef(null);

    /* Locate the target element + recalc on viewport/resize/scroll.
       Retry briefly when the element isn't in the DOM yet — typical when
       we just navigated and the page is still mounting. */
    useLayoutEffect(() => {
        if (!step?.target) {
            setRect(null);
            return undefined;
        }
        let attempts = 0;
        let raf;
        const find = () => {
            const el = document.querySelector(`[data-testid="${step.target}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                // wait a beat for the scroll to settle before measuring
                setTimeout(() => {
                    const r = el.getBoundingClientRect();
                    setRect(r);
                }, 400);
                return;
            }
            // Element not mounted yet — keep retrying for up to ~2s.
            attempts += 1;
            if (attempts < 24) {
                raf = requestAnimationFrame(find);
            } else {
                // Target never appeared — fall back to a centered tooltip
                setRect(null);
            }
        };
        find();
        return () => raf && cancelAnimationFrame(raf);
    }, [step]);

    /* Position the tooltip near the target. Pick the side with the most
       breathing room. Tooltip is ~360×220 — we account for that when
       deciding placement. */
    useLayoutEffect(() => {
        if (!rect) {
            // Centered fallback
            setTooltipPos({
                left: window.innerWidth / 2 - 180,
                top: window.innerHeight / 2 - 110,
                placement: "center",
            });
            return;
        }
        const margin = 16;
        const ttW = 380;
        const ttH = 200;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const spaceRight = window.innerWidth - rect.right;
        const spaceLeft = rect.left;
        let placement = step?.placement || "auto";
        if (placement === "auto") {
            if (spaceBelow >= ttH + margin) placement = "bottom";
            else if (spaceAbove >= ttH + margin) placement = "top";
            else if (spaceRight >= ttW + margin) placement = "right";
            else if (spaceLeft >= ttW + margin) placement = "left";
            else placement = "bottom";
        }
        let left, top;
        if (placement === "bottom") {
            left = Math.max(margin, Math.min(window.innerWidth - ttW - margin, rect.left + rect.width / 2 - ttW / 2));
            top = rect.bottom + margin;
        } else if (placement === "top") {
            left = Math.max(margin, Math.min(window.innerWidth - ttW - margin, rect.left + rect.width / 2 - ttW / 2));
            top = rect.top - ttH - margin;
        } else if (placement === "right") {
            left = rect.right + margin;
            top = Math.max(margin, Math.min(window.innerHeight - ttH - margin, rect.top + rect.height / 2 - ttH / 2));
        } else { // left
            left = rect.left - ttW - margin;
            top = Math.max(margin, Math.min(window.innerHeight - ttH - margin, rect.top + rect.height / 2 - ttH / 2));
        }
        setTooltipPos({ left, top, placement });
    }, [rect, step]);

    /* Recompute on resize so the tour survives a window resize */
    useEffect(() => {
        if (!step?.target) return;
        const recalc = () => {
            const el = document.querySelector(`[data-testid="${step.target}"]`);
            if (el) setRect(el.getBoundingClientRect());
        };
        window.addEventListener("resize", recalc);
        window.addEventListener("scroll", recalc, true);
        return () => {
            window.removeEventListener("resize", recalc);
            window.removeEventListener("scroll", recalc, true);
        };
    }, [step]);

    if (!step) return null;
    const isLast = stepIdx + 1 >= totalSteps;
    const isFirst = stepIdx === 0;

    /* SVG mask: a full-screen rect with a cut-out shaped like the target.
       Falls back to a solid dim layer when no target. */
    const overlayContent = (
        <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="tour-overlay">
            {rect ? (
                <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "auto" }} onClick={(e) => e.stopPropagation()}>
                    <defs>
                        <mask id="tour-mask">
                            <rect x="0" y="0" width="100%" height="100%" fill="white" />
                            <rect
                                x={rect.left - 8}
                                y={rect.top - 8}
                                width={rect.width + 16}
                                height={rect.height + 16}
                                rx="12"
                                fill="black"
                            />
                        </mask>
                    </defs>
                    <rect x="0" y="0" width="100%" height="100%" fill="rgba(8,15,30,0.62)" mask="url(#tour-mask)" />
                    {/* Glowing ring around the spotlight */}
                    <rect
                        x={rect.left - 6}
                        y={rect.top - 6}
                        width={rect.width + 12}
                        height={rect.height + 12}
                        rx="11"
                        fill="none"
                        stroke="rgb(20 184 166)"
                        strokeWidth="2"
                        opacity="0.85"
                        className="animate-pulse"
                    />
                </svg>
            ) : (
                <div className="absolute inset-0 bg-slate-900/55" style={{ pointerEvents: "auto" }} />
            )}

            <div
                ref={tooltipRef}
                className="absolute w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl bg-card border border-border shadow-2xl shadow-primary/10 overflow-hidden"
                style={{ left: tooltipPos.left, top: tooltipPos.top, pointerEvents: "auto" }}
                role="dialog"
                aria-modal="true"
                data-testid="tour-tooltip"
            >
                <div className="flex items-center justify-between px-4 pt-3.5 pb-2 border-b border-border bg-gradient-to-br from-card to-primary/[0.04]">
                    <div className="flex items-center gap-1.5">
                        <Sparkle size={13} weight="fill" className="text-primary" />
                        <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-primary">
                            Product tour · {stepIdx + 1} of {totalSteps}
                        </span>
                    </div>
                    <button
                        onClick={stop}
                        className="w-6 h-6 rounded-md hover:bg-secondary grid place-items-center text-muted-foreground hover:text-foreground transition-colors"
                        title="Close (Esc)"
                        data-testid="tour-close-btn"
                    >
                        <X size={13} />
                    </button>
                </div>

                <div className="p-4">
                    <h3 className="font-display text-base font-bold text-foreground">
                        {step.title}
                    </h3>
                    <p className="text-sm text-foreground/80 leading-snug mt-1.5 whitespace-pre-line">
                        {step.body}
                    </p>
                </div>

                <div className="px-4 py-3 border-t border-border bg-secondary/30 flex items-center justify-between gap-2">
                    {/* Progress dots */}
                    <div className="flex items-center gap-1" aria-hidden="true">
                        {Array.from({ length: totalSteps }).map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === stepIdx ? "w-5 bg-primary" : i < stepIdx ? "w-1.5 bg-primary/60" : "w-1.5 bg-border"
                                }`}
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-1.5">
                        {!isFirst && (
                            <button
                                onClick={prev}
                                className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full hover:bg-secondary text-foreground/80"
                                data-testid="tour-prev-btn"
                            >
                                <ArrowLeft size={12} weight="bold" /> Back
                            </button>
                        )}
                        <button
                            onClick={next}
                            className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground shadow shadow-primary/30 hover:shadow-primary/50 transition-all"
                            data-testid="tour-next-btn"
                        >
                            {isLast ? <><CheckCircle size={12} weight="fill" /> Finish</> : <>Next <ArrowRight size={12} weight="bold" /></>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(overlayContent, document.body);
}
